export interface ConnectorCall {
  icon: ConnectorIconName;
  name: string;
  action: string;
}

export interface TradeIdeaMini {
  label: string;
  entry: string;
  stopLoss: string;
  takeProfit: string;
  confidenceScore: number;
  cta: string;
  href: string;
}

export interface LinkCta {
  label: string;
  detail: string;
  secondaryCta?: string;
  secondaryHref?: string;
  cta: string;
  href: string;
}

export interface RiskFlowHeadline {
  label: string;
  headline: string;
  href: string;
}

export interface OpenChatCta {
  label: string;
  detail: string;
  href: string;
}

export interface ResponseScenario {
  kind: "trade" | "arbitrum" | "riskflow" | "open-chat";
  phrase: string;
  stream: string;
  tools: ConnectorCall[];
  textRows: string[];
  tradeIdea?: TradeIdeaMini;
  linkCta?: LinkCta;
  riskflowHeadline?: RiskFlowHeadline;
  openChat?: OpenChatCta;
}

export type ConnectorIconName = "consul" | "riskflow" | "arbitrum" | "openui";

export const responseScenarios: ResponseScenario[] = [
  {
    kind: "trade",
    phrase: "Preparing trade idea",
    stream: "Checking entry, stop, and target before charting the levels.",
    tools: [
      {
        icon: "consul",
        name: "QuickScout",
        action: "Read active chart level",
      },
      {
        icon: "riskflow",
        name: "RiskFlow",
        action: "Check headline pressure",
      },
    ],
    tradeIdea: {
      label: "NQ long continuation",
      entry: "22,384.25",
      stopLoss: "22,331.50",
      takeProfit: "22,506.00",
      confidenceScore: 7.8,
      cta: "Chart the Trade Idea",
      href: "#chart-trade-idea",
    },
    textRows: [
      "NQ is holding above the marked reclaim.",
      "Momentum stays valid while 22,331.50 holds.",
      "Target is the prior rejection shelf at 22,506.00.",
      "Risk is headline-driven; size should stay reduced.",
      "No broker action is taken from this dock.",
      "Chart the levels before acting.",
    ],
  },
  {
    kind: "arbitrum",
    phrase: "Preparing Arbitrum report",
    stream: "The answer belongs in Arbitrum, so the dock only asks to open it.",
    tools: [
      {
        icon: "arbitrum",
        name: "Arbitrum",
        action: "Fetch latest chamber verdict",
      },
      {
        icon: "consul",
        name: "QuickScout",
        action: "Keep desktop context",
      },
    ],
    linkCta: {
      label:
        "I've prepared a report in the Arbitrum for you to look at. Would you like to open it?",
      detail: "",
      secondaryCta: "Later",
      secondaryHref: "#later",
      cta: "Open Arbitrum",
      href: "#arbitrum-reports",
    },
    textRows: [
      "The chamber is split but leaning risk-on.",
      "Dissent is focused on event timing, not thesis quality.",
      "Catalyst window is compressed into the next session.",
      "Confidence is not high enough for automatic action.",
      "Read the report before changing exposure.",
    ],
  },
  {
    kind: "riskflow",
    phrase: "RiskFlow headline expanded",
    stream:
      "Headline risk is compact enough to keep in the dock and mirror nearby.",
    tools: [
      {
        icon: "riskflow",
        name: "RiskFlow",
        action: "Open live headline card",
      },
    ],
    riskflowHeadline: {
      label: "RiskFlow",
      headline: "Treasury auction tail lifts duration risk into the close.",
      href: "#riskflow-headline",
    },
    textRows: [
      "Duration risk is the active driver.",
      "The move is session-relevant, not a full thesis flip.",
      "Watch rates-sensitive tech first.",
      "Reduce confidence on long-duration entries.",
      "Keep the card pinned while reviewing positions.",
    ],
  },
  {
    kind: "open-chat",
    phrase: "Rich output moved to chat",
    stream: "Tables, charts, and long summaries stay out of the mini dock.",
    tools: [
      {
        icon: "openui",
        name: "OpenUI",
        action: "Route rich answer surface",
      },
    ],
    openChat: {
      label: "",
      detail: "Open Chat for Streamdown tables and charts.",
      href: "#open-chat",
    },
    textRows: [
      "The answer needs more room than the dock has.",
      "Charts and tables stay in Open Chat.",
      "The dock only keeps the route and short status.",
      "Use text reply mode for concise CAO fallback rows.",
      "Voice remains muted while the text request is prepared.",
    ],
  },
];
